import { useConsumptionData } from "@/api/consumption";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { formatCurrency } from "@/lib/format-for-view";
import { format, sub } from "date-fns";
import { TrendingDown, TrendingUp } from "lucide-react";
import {
	Bar,
	BarChart,
	LabelList,
	ResponsiveContainer,
	Tooltip,
	type TooltipProps,
	XAxis,
	YAxis,
} from "recharts";

interface DayData {
	day: string;
	peak: number;
	offPeak: number;
	total: number;
	subscription: number;
}

const CostBreakdownChart = () => {
	const endDate = sub(new Date(), { days: 2 });
	const startDate = sub(endDate, { days: 14 });

	const {
		data: apiData,
		isLoading,
		error,
	} = useConsumptionData({
		energy: "ELEC",
		start_date: startDate.toISOString().split("T")[0],
		end_date: endDate.toISOString().split("T")[0],
		granularity: "DAILY",
		include_baseline: true,
	});

	// Transform the data to match the required format
	const data: DayData[] =
		apiData?.data?.map((item) => {
			const date = new Date(item.timestamp);
			const day = date.toISOString();

			// If we have peak/off-peak data, use it, otherwise split the cost evenly
			const peak = item.cost_hp || item.cost / 2;
			const offPeak = item.cost_hc || item.cost / 2;

			return {
				day,
				peak,
				offPeak,
				total: item.cost + item.subscription_cost,
				subscription: item.subscription_cost,
			};
		}) || [];

	// Calculate percentage change from previous period (last 7 days vs previous 7 days)
	const calculatePercentageChange = () => {
		if (!data || data.length < 14) return 0;

		// The first 7 items are the previous period, the last 7 are the current period
		// Since the data is likely sorted by date (oldest to newest)
		const previousPeriodData = data.slice(0, 7);
		const currentPeriodData = data.slice(-7);

		const previousPeriodTotal = previousPeriodData.reduce(
			(sum, item) => sum + item.total,
			0,
		);

		const currentPeriodTotal = currentPeriodData.reduce(
			(sum, item) => sum + item.total,
			0,
		);

		if (previousPeriodTotal === 0) return 0;
		return (
			((currentPeriodTotal - previousPeriodTotal) / previousPeriodTotal) * 100
		);
	};

	const percentageChange = calculatePercentageChange();
	const hasValidTrendData = data.length >= 14;

	// Only show the last 7 days in the chart
	const chartData = data.slice(-7);

	// Dynamically determine Y-axis max based on highest total
	const maxTotal = Math.max(...chartData.map((item) => item.total));
	const paddedMax = Math.ceil((maxTotal * 1.1) / 4) * 4;
	const tickStep = paddedMax / 2;

	const CustomTooltip = ({
		active,
		payload,
		label,
	}: TooltipProps<number, string>) => {
		if (active && payload && payload.length) {
			return (
				<div className="bg-white dark:bg-gray-800 p-2 rounded-lg shadow-lg border border-gray-100 dark:border-gray-700 text-xs max-w-[165px]">
					<p className="font-semibold text-xs mb-1">
						{`${new Date(label).toLocaleDateString("fr-FR", {
							weekday: "short",
							month: "numeric",
							day: "numeric",
						})}: ${formatCurrency(payload[0].payload.total)}`}
					</p>
					<div className="space-y-1">
						<div className="flex items-center justify-between gap-2">
							<div className="flex items-center gap-1">
								<div className="w-2 h-2 rounded-sm bg-teal-400" />
								<span className="text-gray-600 dark:text-gray-300 text-xs">
									Heures Creuses
								</span>
							</div>
							<span className="font-medium text-xs">
								{formatCurrency(payload[0].payload.offPeak)}
							</span>
						</div>
						<div className="flex items-center justify-between gap-2">
							<div className="flex items-center gap-1">
								<div className="w-2 h-2 rounded-sm bg-pink-500" />
								<span className="text-gray-600 dark:text-gray-300 text-xs">
									Heures Pleines
								</span>
							</div>
							<span className="font-medium text-xs">
								{formatCurrency(payload[0].payload.peak)}
							</span>
						</div>
						<div className="flex items-center justify-between gap-2">
							<div className="flex items-center gap-1">
								<div className="w-2 h-2 rounded-sm bg-[#0F172A]" />
								<span className="text-gray-600 dark:text-gray-300 text-xs">
									Abonnement
								</span>
							</div>
							<span className="font-medium text-xs">
								{formatCurrency(payload[0].payload.subscription)}
							</span>
						</div>
					</div>
				</div>
			);
		}
		return null;
	};

	if (apiData?.data.length === 0) {
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="text-red-500">
						Aucune donnée trouvée pour cette période
					</div>
				</CardContent>
			</Card>
		);
	}

	if (isLoading) {
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="animate-pulse space-y-4">
						<div className="h-4 bg-gray-200 rounded w-1/4" />
						<div className="h-6 bg-gray-200 rounded w-3/4" />
						<div className="h-8 bg-gray-200 rounded w-1/2" />
						<div className="h-4 bg-gray-200 rounded w-2/3" />
					</div>
				</CardContent>
			</Card>
		);
	}

	if (error) {
		console.error("Error fetching consumption data:", error);
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="text-red-500">
						Erreur lors du chargement des données de consommation. Veuillez
						réessayer ultérieurement.
					</div>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card className="w-full max-w-full overflow-hidden border-gray-200 dark:border-gray-800 shadow-sm">
			<CardHeader className="pb-1 pt-3 px-3 sm:px-4">
				<div className="flex justify-between items-center">
					<div>
						<CardTitle className="text-base sm:text-lg font-bold">
							Dépenses quotidiennes
						</CardTitle>
						<CardDescription className="text-xs mt-0.5">
							Les 7 derniers jours
						</CardDescription>
					</div>
				</div>
			</CardHeader>

			<CardContent className="p-2 sm:p-3">
				<div className="h-40 sm:h-52 w-full">
					<ResponsiveContainer width="100%" height="100%">
						<BarChart
							data={chartData}
							margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
							barGap={0}
							barCategoryGap={6}
						>
							<XAxis
								dataKey="day"
								axisLine={false}
								tickLine={false}
								interval={0}
								tick={({ x, y, payload }) => {
									return (
										<>
											<text
												x={x}
												y={y + 10}
												textAnchor="middle"
												fontSize={10}
												fill="#555"
												width={0}
											>
												{new Date(payload.value).toLocaleDateString("fr-FR", {
													weekday: "short",
												})}
											</text>
											<text
												x={x}
												y={y + 25}
												textAnchor="middle"
												fontSize={10}
												fill="#666"
												width={0}
											>
												{format(new Date(payload.value), "dd/MM")}
											</text>
										</>
									);
								}}
							/>
							<YAxis
								axisLine={false}
								tickLine={true}
								tick={{ fontSize: 10, fill: "#666" }}
								tickFormatter={(value) => value}
								width={30}
								domain={[0, paddedMax]}
								ticks={[0, tickStep, paddedMax]}
							/>
							<YAxis
								yAxisId="grid"
								orientation="right"
								tickLine={false}
								axisLine={false}
								tick={false}
								width={0}
							/>
							<Tooltip
								content={<CustomTooltip />}
								cursor={{ fill: "rgba(0, 0, 0, 0.04)" }}
							/>

							<Bar
								dataKey="subscription"
								stackId="a"
								fill="#0F172A"
								animationDuration={1200}
								label="Abonnement"
							/>
							<Bar
								dataKey="peak"
								stackId="a"
								fill="rgba(236, 72, 153, 0.85)"
								animationDuration={1200}
							/>
							<Bar
								dataKey="offPeak"
								stackId="a"
								fill="rgba(45, 212, 191, 0.85)"
								radius={[4, 4, 0, 0]}
								animationDuration={1200}
							>
								<LabelList
									dataKey="total"
									position="top"
									formatter={(value: number) => formatCurrency(value)}
									className="text-xs fill-gray-700 dark:fill-gray-300"
								/>
							</Bar>
						</BarChart>
					</ResponsiveContainer>
				</div>

				{/* Legend */}
				<div className="flex justify-center mt-2 gap-4">
					<div className="flex items-center">
						<div className="w-2.5 h-2.5 rounded-sm bg-teal-400 mr-1.5" />
						<span className="text-xs text-gray-600 dark:text-gray-300">
							Heures Creuses
						</span>
					</div>
					<div className="flex items-center">
						<div className="w-2.5 h-2.5 rounded-sm bg-pink-500 mr-1.5" />
						<span className="text-xs text-gray-600 dark:text-gray-300">
							Heures Pleines
						</span>
					</div>
					<div className="flex items-center">
						<div className="w-2.5 h-2.5 rounded-sm bg-[#0F172A] mr-1.5" />
						<span className="text-xs text-gray-600 dark:text-gray-300">
							Abonnement
						</span>
					</div>
				</div>

				{/* Trending */}
				{hasValidTrendData && (
					<div className="mt-3 p-2 sm:p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700">
						<div className="flex items-center justify-center gap-1 text-xs font-medium text-gray-800 dark:text-gray-200">
							{percentageChange <= 0 ? (
								<TrendingDown className="h-3.5 w-3.5 text-green-500" />
							) : (
								<TrendingUp className="h-3.5 w-3.5 text-red-500" />
							)}
							{percentageChange <= 0 ? (
								<span className="text-green-500">
									Vos dépenses ont diminué de{" "}
									{Math.abs(percentageChange).toFixed(1)}% par rapport aux 7
									jours précédents
								</span>
							) : (
								<span className="text-red-500">
									Vos dépenses ont augmenté de{" "}
									{Math.abs(percentageChange).toFixed(1)}% par rapport aux 7
									jours précédents
								</span>
							)}
						</div>
					</div>
				)}
			</CardContent>
		</Card>
	);
};

export default CostBreakdownChart;
